import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { SeoMetaTag } from 'types/seo-meta-tag';

export type SeoProps = {
  title: string;
  useTitleOverSeoTitle?: boolean;
  pageSeoMetaTags?: Record<string, unknown> | null | undefined;
};

type SiteMetadata = {
  title: string;
  siteUrl: string;
  description: string;
};

/**
 * SEO component
 * see: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */
const Seo: React.FC<React.PropsWithChildren<SeoProps>> = ({ title, useTitleOverSeoTitle, pageSeoMetaTags, children }) => {

  const { site, datoCmsWebsiteLayout } = useStaticQuery<Queries.SiteDataQuery>(graphql`
    query SiteData {
      site {
        siteMetadata {
          title
          siteUrl
          description
        }
      }
      datoCmsWebsiteLayout {
        ... WebsiteLayoutSettings
      }
    }
  `)

  const tags = pageSeoMetaTags || datoCmsWebsiteLayout?.seoMetaTags?.tags;

  // convert tags to SeoMetaTag[]
  const convertedMetaTags = convertTagsToSeoMetaTags(tags);

  const siteTitle = datoCmsWebsiteLayout?.seo?.title || site?.siteMetadata?.title || 'Washington State Fair Events Center';

  // if pageSeoMetaTags is provided, get the title from the meta tags
  const seoPageTitle = pageSeoMetaTags && getTitleFromMetaTags(convertedMetaTags);

  // Check for useTitleOverSeoTitle. Otherwise, The Seo Page Title takes precedence over the title prop
  const pageTitle = useTitleOverSeoTitle ? title : seoPageTitle || title;

  const fullTitle = pageTitle ? `${pageTitle} | ${siteTitle}` : siteTitle;

  const metaTagElements = getMetaTagElements(convertedMetaTags);

  return (
    <>
      <html lang="en" />
      <title>{fullTitle}</title>
      {metaTagElements}

      {/* ADOBE FONT: Proxima Nova - 400, 400 italic, 700, 700 italic, 900 */}
      <link rel="stylesheet" id="proxima-nova" href="https://use.typekit.net/ciw8dbn.css" />
      {children}
    </>

  )
}

export default Seo;

const convertTagsToSeoMetaTags = (tags: Record<string, unknown> | null | undefined): SeoMetaTag[] => {
  if (!tags || !Array.isArray(tags)) {
    return [];
  }

  return tags.map(tag => {
    if (typeof tag !== 'object' || tag === null) {
      return null;
    }

    const { tagName, content, attributes } = tag as { tagName: string; content?: string; attributes?: Record<string, string> };

    const seoMetaTag: SeoMetaTag = { tagName };

    if (content) {
      seoMetaTag.content = content;
    }

    if (attributes) {
      seoMetaTag.attributes = attributes;
    }

    return seoMetaTag;
  }).filter((tag): tag is SeoMetaTag => tag !== null);
};

const getMetaTagElements = (metaTags: SeoMetaTag[]) => {
  return metaTags
    .filter(tag => tag.tagName === 'meta')
    .map((tag, index) => {
      const attributes = { ...tag.attributes };
      if (attributes && attributes.content !== undefined) {
        attributes.content = String(attributes.content);
      }
      return (
        <meta key={index} {...attributes} />
      );
    });
};

const getTitleFromMetaTags = (metaTags: SeoMetaTag[]) => {
  const titleTag = metaTags.find(tag => tag.tagName === 'title');
  return titleTag?.content || '';
}